import { BlogCard } from '@blog/components/display/cards';
import { BlogHero } from '@blog/components/display/heros';
import { useGetBlogPostBySlugQuery, useGetCategoriesQuery } from '@blog/services';
import { mapCategoriestoName } from '@blog/utils/blog';
import { HtmlBlock } from '@components/display/richTexts/RichTextBlock';
import { APP_PREFIX_PATH } from '@constants/common';
import { useAppDispatch } from '@hooks/generic';
import { usePrefixedNavigate } from '@hooks/usePrefixedNavigate';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { Button, Typography } from '@mui/material';
import { setBreadcrumbs } from '@store/breadcrumbSlice';
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import { NotFound } from 'src/routes/NotFound';

export const BlogDetail = () => {
    const { slug } = useParams();
    const { data: post, isLoading: postLoading } = useGetBlogPostBySlugQuery(slug ?? '', { skip: !Boolean(slug) });
    const { data: blogCategories } = useGetCategoriesQuery();

    const contentRef = useRef<HTMLDivElement>(null);

    const navigate = usePrefixedNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const intl = useIntl();

    const goToBlogList = useCallback(() => {
        // Check if we visited this page from within the website
        // https://github.com/remix-run/history/blob/main/docs/api-reference.md#locationkey
        const historyEntryExists = location.key !== 'default';

        if (historyEntryExists) {
            // Just pop if there's a history
            return navigate(-1);
        }
        // Otherwise navigate normally
        return navigate('/blog');
    }, [location.key, navigate]);

    useEffect(() => {
        const scrollElement = document.querySelector('#react-page');
        if (scrollElement) {
            scrollElement.scrollTo({ top: 0 });
        }
    }, [slug]);

    useEffect(() => {
        dispatch(
            setBreadcrumbs([
                {
                    label: intl.formatMessage({ id: 'navbar.blog', defaultMessage: 'Blog' }),
                    to: `${APP_PREFIX_PATH}/blog`,
                },
                {
                    label: post?.title
                        ? post.title
                        : intl.formatMessage({
                              id: postLoading ? 'loading' : 'notFound',
                              defaultMessage: postLoading ? 'Loading' : 'Not Found',
                          }),
                    to: `${APP_PREFIX_PATH}/blog/${slug}`,
                    loading: postLoading,
                },
            ])
        );
    }, [dispatch, post?.title, postLoading, slug, intl]);

    if (!postLoading) {
        if (post && blogCategories) {
            const { author, categories: postCategories, content, date, title } = post;
            const mappedCategories = mapCategoriestoName(postCategories, blogCategories);
            return (
                <>
                    <title>
                        {post?.title ??
                            intl.formatMessage({
                                id: 'blog.browserTitle',
                                defaultMessage: 'Blog - Victron Professional',
                            })}
                    </title>
                    <div className="mt-10 pb-10 px-4 sm:px-8" ref={contentRef}>
                        <Button color="neutral-700" className="grow rounded-full" onClick={goToBlogList}>
                            <ArrowBack className="mr-2" />
                            <FormattedMessage id="common.goBack" defaultMessage="Go Back" />
                        </Button>
                        <BlogHero
                            title={title}
                            author={author}
                            date={date}
                            categories={mappedCategories}
                            className="my-12"
                            key={slug}
                        />

                        <article>
                            <HtmlBlock htmlString={content} />
                        </article>
                    </div>
                    <div className="bg-[#f4f3f1] mt-32 py-10">
                        <div className="px-4 px-4 sm:px-8">
                            <Typography variant="h3" className="font-museo font-bold text-3xl lg:text-4xl">
                                <FormattedMessage id="blog.relatedPosts" defaultMessage="Related Blog Posts" />
                            </Typography>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12 mt-8">
                                {post.related_posts.map((post) => (
                                    <BlogCard
                                        author={[post.author_name]}
                                        categories={post.categories.map((cat) => cat.name)}
                                        date={post.date}
                                        title={post.title}
                                        summary={post.excerpt}
                                        slug={post.slug}
                                        image={{ url: post.featured_image ?? '', alt_text: post.title }}
                                        key={post.id}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            );
        }
        return <NotFound />;
    }
    return null;
};
